import { useState, useEffect, useRef } from 'react';
import { gradeApi } from '../../../api.js';
import { REGRADE_POLLING_INTERVAL, MAX_POLLING_TIME } from './constants.js';
import toast from 'react-hot-toast';

const useAiRegrade = (assignment_id, user, getQuestions, getAssignmentSubmission) => {
  // Track regrade polling
  const regradePollingTimers = useRef({});

  // State for AI Edit Popup
  const [showAiEditPopup, setShowAiEditPopup] = useState(false);
  const [aiEditInput, setAiEditInput] = useState('');
  const [isProcessingAiEdit, setIsProcessingAiEdit] = useState(false);
  const [activeSubQuestion, setActiveSubQuestion] = useState(null);

  // Add state variables for solution editing
  const [editingSolutionId, setEditingSolutionId] = useState(null);
  const [editedSolutions, setEditedSolutions] = useState({});
  const [editingCriteriaId, setEditingCriteriaId] = useState(null);
  const [changedMaxWeights, setChangedMaxWeights] = useState({});

  // Function to start polling for regrade completion
  const startPollingForRegradeCompletion = subquestionId => {
    // Clear any existing timer first to prevent duplicates
    if (regradePollingTimers.current[subquestionId]) {
      clearInterval(regradePollingTimers.current[subquestionId]);
      delete regradePollingTimers.current[subquestionId];
    }

    let pollingCount = 0;
    const startTime = Date.now();

    console.log(`Starting polling for regrade completion of subquestion ${subquestionId}`);

    const checkRegradeStatus = async () => {
      try {
        // Fetch the latest questions to check regrade status
        const response = await gradeApi.get({
          path: `/api/assignment/${assignment_id}/questions/`,
          config: {
            headers: {
              Authorization: `Bearer ${user?.session_token}`
            }
          }
        });

        // Find the subquestion in the response to check its regrade_in_progress status
        let isStillRegrading = false;
        if (response && response.questions) {
          for (const question of response.questions) {
            const subQ = question.subQuestions?.find(sq => sq.id == subquestionId);
            if (subQ) {
              isStillRegrading = subQ.regrade_in_progress || false;
              break;
            }
          }
        }

        if (!isStillRegrading) {
          console.log(`Regrade for subquestion ${subquestionId} completed, refreshing all data...`);

          // Clear the polling timer first
          clearInterval(regradePollingTimers.current[subquestionId]);
          delete regradePollingTimers.current[subquestionId];

          // Refresh all data
          if (getQuestions) {
            await getQuestions();
          }

          if (getAssignmentSubmission) {
            await getAssignmentSubmission();
          }

          // Show success message
          // toast.success('AI-Regrade completed! Scores and feedback have been updated.');
        } else if (Date.now() - startTime > MAX_POLLING_TIME) {
          // Stop polling if maximum time reached
          console.log(`Reached maximum polling time for subquestion ${subquestionId}`);
          clearInterval(regradePollingTimers.current[subquestionId]);
          delete regradePollingTimers.current[subquestionId];

          // Show timeout message
          toast.error(
            'Regrade is taking longer than expected. Please refresh the page to check the status.'
          );
        }

        pollingCount++;
      } catch (error) {
        console.error('Error checking regrade status:', error);

        // On error, stop polling after a few retries
        if (pollingCount > 3) {
          clearInterval(regradePollingTimers.current[subquestionId]);
          delete regradePollingTimers.current[subquestionId];
          toast.error('Error checking regrade status. Please refresh the page.');
        }
      }
    };

    // Start polling immediately
    checkRegradeStatus();

    // Then set up interval for subsequent checks
    regradePollingTimers.current[subquestionId] = setInterval(
      checkRegradeStatus,
      REGRADE_POLLING_INTERVAL
    );
  };

  // Clean up polling timers on component unmount
  useEffect(() => {
    return () => {
      // Clear all regrade polling timers
      Object.values(regradePollingTimers.current).forEach(timer => {
        clearInterval(timer);
      });
    };
  }, []);

  const handleAiEditClick = subQuestion => {
    setActiveSubQuestion(subQuestion);
    setShowAiEditPopup(true);
    setAiEditInput('');
  };

  const enhance_single_rubric = async (examPaper, updateExamPaper, uploadQuestions) => {
    if (!activeSubQuestion) {
      toast.error('No active subquestion selected for AI enhancement.');
      return;
    }

    setIsProcessingAiEdit(true);

    try {
      // Determine the question text from available sources
      let questionText = '';
      let solution = activeSubQuestion.solution || '';
      let weight = Number(activeSubQuestion.max_weight) || 0;

      // Try to get question text from examPaper
      if (Array.isArray(examPaper) && examPaper.length > 0) {
        // Find the parent question in the array
        const foundQuestion = examPaper.find(
          q => q.subQuestions && q.subQuestions.some(sq => sq.id === activeSubQuestion.id)
        );
        if (foundQuestion) {
          questionText = foundQuestion.description;
        }
      }

      // Final fallback - use a placeholder
      if (!questionText) {
        toast('Using default question text. AI enhancement may be less effective.', {
          icon: '⚠️'
        });
        questionText = 'Question text not available';
      }

      const payload = {
        instruction: aiEditInput,
        questionText: questionText,
        solution: solution,
        weight: Number(weight)
      };

      console.log('Sending payload to AI enhance-rubric:', payload);

      const response = await gradeApi.post({
        path: '/api/ai/enhance-rubric/',
        body: payload,
        config: {
          headers: {
            Authorization: `Bearer ${user.session_token}`
          }
        }
      });

      console.log('AI enhancement response:', response.data);

      // Extract the enhanced rubric data from the response
      // Handle different response structures
      const enhancedRubric = response.data.data || response.data;

      if (enhancedRubric) {
        // Get the grading criteria from the response
        const gradingCriteria = enhancedRubric.gradingCriteria || enhancedRubric.grading_criteria;
        // Get the weight from the response
        const questionWeight = enhancedRubric.questionWeight || enhancedRubric.weight;

        if (gradingCriteria) {
          // Update the active subquestion with the enhanced rubric data
          const updatedSubQuestion = {
            ...activeSubQuestion,
            grading_criteria: gradingCriteria
          };

          // Update weight if it was changed
          if (questionWeight) {
            updatedSubQuestion.max_weight = Number(questionWeight);
          }

          console.log('Updated subquestion:', updatedSubQuestion);

          // Update the editedSolutions state with the new grading criteria
          setEditedSolutions(prev => ({
            ...prev,
            [activeSubQuestion.id]: gradingCriteria
          }));

          // Update the DOM element's data-max-weight attribute
          if (questionWeight) {
            const element = document.getElementById(`subquestion-${activeSubQuestion.id}`);
            if (element) {
              element.setAttribute('data-max-weight', Number(questionWeight).toString());
            }

            // Mark this weight as changed
            setChangedMaxWeights(prev => ({
              ...prev,
              [activeSubQuestion.id]: true
            }));
          }

          // Find and update the subquestion in the examPaper
          if (Array.isArray(examPaper)) {
            // For array structure
            const updatedExamPaper = examPaper.map(question => {
              if (
                question.subQuestions &&
                question.subQuestions.some(sq => sq.id === activeSubQuestion.id)
              ) {
                return {
                  ...question,
                  subQuestions: question.subQuestions.map(sq =>
                    sq.id === activeSubQuestion.id ? updatedSubQuestion : sq
                  )
                };
              }
              return question;
            });

            // If we have a function to update the examPaper state, call it
            if (updateExamPaper) {
              updateExamPaper(updatedExamPaper);
            }
          }

          // Call uploadQuestions directly to save changes to backend
          if (uploadQuestions) {
            await uploadQuestions();
          }

          toast.success('Rubric enhanced successfully!');
        } else {
          toast.error('The AI response did not contain updated grading criteria.');
        }
      } else {
        toast.error('Failed to enhance rubric. Please try again.');
      }
    } catch (error) {
      console.error('Error while processing AI edit:', error);
      toast.error(
        error.response?.data?.error || 'Failed to process your request. Please try again.'
      );
    } finally {
      setIsProcessingAiEdit(false);
      setShowAiEditPopup(false);
      setAiEditInput('');
      setActiveSubQuestion(null);
    }
  };

  // Handler for toggling the solution editor
  const toggleSolutionEditor = (subQuestionId, examPaper) => {
    if (editingSolutionId === subQuestionId) {
      setEditingSolutionId(null);
      setEditingCriteriaId(null);
      // Reset changed max weights state when closing editor
      setChangedMaxWeights({});
    } else {
      setEditingSolutionId(subQuestionId);
      // Reset changed max weights state when opening editor
      setChangedMaxWeights({});

      // When opening the editor, reset the displayed max weight to the original value
      const element = document.getElementById(`subquestion-${subQuestionId}`);
      if (element) {
        // Find the original max_weight from the subquestion object
        const subQ = examPaper.flatMap(q => q.subQuestions).find(sq => sq.id === subQuestionId);
        const originalMaxWeight = subQ?.max_weight || 1;
        // Set the data attribute to the original value
        element.setAttribute('data-max-weight', originalMaxWeight.toString());
      }

      // Initialize the edited solution if not already present
      if (!editedSolutions[subQuestionId]) {
        const subQ = examPaper.flatMap(q => q.subQuestions).find(sq => sq.id === subQuestionId);

        setEditedSolutions(prev => ({
          ...prev,
          [subQuestionId]: subQ?.solution || ''
        }));
      }
    }
  };

  const handleSaveSolution = async (subQuestionId, submissionIds, examPaper) => {
    try {
      // Find the subquestion in the exam paper
      const subQ = examPaper.flatMap(q => q.subQuestions).find(sq => sq.id === subQuestionId);

      // Get the updated max_weight from our data attribute if it exists
      let updatedMaxWeight = subQ?.max_weight || 1;
      const element = document.getElementById(`subquestion-${subQuestionId}`);

      if (element) {
        // First check for our data attribute where we stored the most recent value
        const dataMaxWeight = element.getAttribute('data-max-weight');
        if (dataMaxWeight) {
          updatedMaxWeight = parseFloat(dataMaxWeight);
        }
      }

      // If we couldn't find the value in the DOM, use the edited solutions state
      if (updatedMaxWeight === null && editedSolutions[subQuestionId]) {
        updatedMaxWeight = parseFloat(tempMaxScores[subQuestionId]);
      }

      // If we still don't have a max weight, use a default of 5
      if (updatedMaxWeight === null || isNaN(updatedMaxWeight)) {
        updatedMaxWeight = 5; // Default
      }

      // Build the request body with optional submissionIds
      const requestBody = {
        subQuestionId: subQuestionId,
        gradingCriteria: subQ?.gradingCriteria || '',
        solution: editedSolutions[subQuestionId],
        maxWeight: updatedMaxWeight
      };

      // Only add submissionIds if they are provided
      if (submissionIds && submissionIds.length > 0) {
        requestBody.submissionIds = submissionIds;
      }

      // Use the same API endpoint as in handleConfirmRegrade
      await gradeApi.post({
        path: `/api/assignment/${assignment_id}/ai-regrade/`,
        body: requestBody,
        config: {
          headers: {
            Authorization: `Bearer ${user?.session_token}`
          }
        }
      });

      // Close the solution editor
      setEditingSolutionId(null);
      setEditingCriteriaId(null);

      // Display success message using toast
      toast.success('AI-Regrade initiated. The system will update the grades shortly.');

      // Start polling for regrade completion
      startPollingForRegradeCompletion(subQuestionId);

      // Refresh data
      if (getQuestions) {
        getQuestions();
      }

      if (getAssignmentSubmission) {
        getAssignmentSubmission();
      }
    } catch (error) {
      console.error('AI-Regrade failed:', error);
      toast.error('AI-Regrade failed. Please try again.');
    }
  };

  // Handler for updating the solution text
  const handleSolutionChange = (subQuestionId, value) => {
    setEditedSolutions(prev => ({
      ...prev,
      [subQuestionId]: value
    }));

    // Mark this subquestion as having its criteria being edited
    setEditingCriteriaId(subQuestionId);
  };

  // Function to handle AI regrade action
  const handleAIRegrade = async (subQuestionId, examPaper) => {
    try {
      // Get the max weight value from the DOM
      const element = document.getElementById(`subquestion-${subQuestionId}`);
      let maxWeight = null;
      if (element) {
        const dataMaxWeight = element.getAttribute('data-max-weight');
        if (dataMaxWeight) {
          maxWeight = parseFloat(dataMaxWeight);
        }
      }

      if (maxWeight === null || isNaN(maxWeight)) {
        maxWeight = 5; // Default
      }

      const subQ = examPaper.flatMap(q => q.subQuestions).find(sq => sq.id === subQuestionId);

      const requestBody = {
        subQuestionId: subQuestionId,
        gradingCriteria: subQ?.gradingCriteria || '',
        solution: editedSolutions[subQuestionId],
        maxWeight: maxWeight
      };

      // Send to API and wait for success response
      const response = await gradeApi.post({
        path: `/api/assignment/${assignment_id}/ai-regrade/`,
        body: requestBody,
        config: {
          headers: {
            Authorization: `Bearer ${user?.session_token}`
          }
        }
      });

      // Only proceed if we got a successful response
      if (response && (response.status === 200 || response.status === 201 || response.success)) {
        // Close the solution editor
        setEditingSolutionId(null);
        setEditingCriteriaId(null);

        // Display success message
        toast.success('AI-Regrade initiated. The system will update the grades shortly.');

        // Start polling for regrade completion
        startPollingForRegradeCompletion(subQuestionId);

        // Initial data refresh to get the updated regradeInProgress status
        if (getQuestions) {
          await getQuestions();
        }
      } else {
        throw new Error('Failed to initiate regrade');
      }
    } catch (error) {
      console.error('AI-Regrade failed:', error);
      toast.error('AI-Regrade failed. Please try again.');
    }
  };

  return {
    // AI Edit popup state
    showAiEditPopup,
    aiEditInput,
    setAiEditInput,
    isProcessingAiEdit,
    activeSubQuestion,

    // Solution editing state
    editingSolutionId,
    editedSolutions,
    editingCriteriaId,
    changedMaxWeights,
    setEditingCriteriaId,
    setChangedMaxWeights,

    // Functions
    handleAiEditClick,
    enhance_single_rubric,
    toggleSolutionEditor,
    handleSaveSolution,
    handleSolutionChange,
    handleAIRegrade,
    startPollingForRegradeCompletion,

    // Handlers for popup
    handleAiEditPopupCancel: () => {
      setShowAiEditPopup(false);
      setAiEditInput('');
      setActiveSubQuestion(null);
    },
    handleAiEditInputChange: (e) => setAiEditInput(e.target.value)
  };
};

export default useAiRegrade;